import { FC, useEffect, useState } from 'react';

import { Box, useMediaQuery } from '@mui/material';
import { IYearPerformance } from 'portfolio3/features/performance/containers/PerformanceContainer.types';
import { emitYMEvent } from 'portfolio3/features/yandexMetrika';
import { commonTheme } from 'portfolio3/styles/theme';

import PolarAreaChart from '../../common/PolarAreaChart';
import ShowMore from '../../common/ShowMore';
import SubjectRow from './subjectRow';

import './index.scss';

const COLORS = [
  '#FFE8CC',
  '#FFDEEB',
  '#F3D9FA',
  '#E5DBFF',
  '#DBE4FF',
  '#D0EBFF',
  '#C5F6FA',
  '#C3FAE8',
  '#D3F9D8',
  '#E9FAC8',
  '#FFF3BF',
];
const ACTIVE_COLORS = [
  '#FFC078',
  '#FAA2C1',
  '#E599F7',
  '#B197FC',
  '#91A7FF',
  '#74C0FC',
  '#66D9E8',
  '#63E6BE',
  '#8CE99A',
  '#C0EB75',
  '#FFE066',
];

function extendArrayWithLastItem<T>(array: T[], desiredLength: number) {
  return array.length < desiredLength
    ? [...array, ...Array.from({ length: desiredLength - array.length }, () => array[array.length - 1])]
    : array;
}

interface IPerformanceDiagram {
  yearPerformance: IYearPerformance;
  selectedSubjectId: number | undefined;
  onSelectSubject: (id: number) => void;
  isInteger?: boolean;
}

const PerformanceDiagram: FC<IPerformanceDiagram> = ({
  yearPerformance,
  selectedSubjectId,
  onSelectSubject,
  isInteger = false,
}) => {
  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));

  const initialSubjectsDisplayCount = 5;
  const [subjectsDisplayCount, setSubjectsDisplayCount] = useState(initialSubjectsDisplayCount);

  const { averageMarkFive, subjectsPerformance } = yearPerformance ?? {};

  const totalSubjectsCount = subjectsPerformance?.length ?? 0;
  const subjectColors = extendArrayWithLastItem(COLORS, totalSubjectsCount);
  const subjectActiveColors = extendArrayWithLastItem(ACTIVE_COLORS, totalSubjectsCount);
  const hiddenSubjectsCount = totalSubjectsCount - initialSubjectsDisplayCount;
  const isAllSubjectsShown = totalSubjectsCount === subjectsDisplayCount;

  useEffect(() => {
    setSubjectsDisplayCount(initialSubjectsDisplayCount);
  }, [yearPerformance]);

  const handleShowAllSubjects = () => {
    emitYMEvent({ type: 'listItemsDisclosure' });
    setSubjectsDisplayCount(totalSubjectsCount);
  };
  const handleShowInitialSubjects = () => setSubjectsDisplayCount(initialSubjectsDisplayCount);

  // chart data
  const chartAverage = averageMarkFive;
  const chartLabels = subjectsPerformance?.map((subject) => subject.subjectName);
  const chartData = subjectsPerformance
    ?.map((subject) => subject.markValue)
    ?.filter((val): val is number => typeof val === 'number');
  const chartColors = subjectColors.map((color, index) => {
    const selectedSubjectIndex = subjectsPerformance?.findIndex((subject) => subject.subjectId === selectedSubjectId);
    if (selectedSubjectIndex === -1 || selectedSubjectIndex === index) return subjectActiveColors[index];
    return color;
  });

  return (
    <div className="performance-diagram-wrapper">
      <div className="performance-diagram">
        <div className="performance-diagram__chart-container">
          <PolarAreaChart
            average={chartAverage ? chartAverage.toFixed(2) : undefined}
            labels={chartLabels ?? []}
            data={chartData ?? []}
            colors={chartColors}
            min={0}
            max={5.1}
            stepSize={1}
          />
          <div className="performance-diagram__subjects">
            {subjectsPerformance?.slice(0, subjectsDisplayCount).map((subject, index) => {
              const { subjectId, subjectName, markValue } = subject;

              return (
                <SubjectRow
                  key={subjectId + subjectName}
                  name={subjectName}
                  mark={markValue}
                  markChange={0}
                  color={subjectActiveColors[index]}
                  selected={subjectId === selectedSubjectId}
                  onSubjectSelected={() => onSelectSubject(subjectId)}
                  isInteger={isInteger}
                />
              );
            })}
            <ShowMore
              hiddenCount={hiddenSubjectsCount}
              isAllShown={isAllSubjectsShown}
              onShowInitialItems={handleShowInitialSubjects}
              onShowAllItems={handleShowAllSubjects}
            />
          </div>
        </div>
        {/* <div className="performance-diagram__details-container">
          <SubjectThemesContainer selectedSubjectId={selectedSubjectId} yearPerformance={yearPerformance} />
        </div> */}
      </div>
      {/* {isMobile && (
        <Box marginTop="12px">
          <SubjectHint />
        </Box>
      )} */}
    </div>
  );
};

export default PerformanceDiagram;
